import { Route, Switch, useLocation } from "wouter";
import { lazy, Suspense } from "react";
import PageLoader from "./components/PageLoader";
import AppShell from "./AppShell";

// Critical path: loaded eagerly so the homepage hydrates against SSR HTML
import Home from "./pages/Home";
import NotFound from "@/pages/NotFound";

const Uterum = lazy(() => import("./pages/Uterum"));
const Pris = lazy(() => import("./pages/Pris"));
const Bygglov = lazy(() => import("./pages/Bygglov"));
const Vinterbonat = lazy(() => import("./pages/Vinterbonat"));
const Glaspartier = lazy(() => import("./pages/Glaspartier"));
const Inspiration = lazy(() => import("./pages/Inspiration"));
const Grund = lazy(() => import("./pages/Grund"));
const Tak = lazy(() => import("./pages/Tak"));
const Uppvarmning = lazy(() => import("./pages/Uppvarmning"));
const Kondens = lazy(() => import("./pages/Kondens"));
const Oisolerat = lazy(() => import("./pages/Oisolerat"));
const Checklista = lazy(() => import("./pages/Checklista"));
const CaseHub = lazy(() => import("./pages/CaseHub"));
const CityPage = lazy(() => import("./pages/CityPage"));
const UterumVsOrangeri = lazy(() => import("./pages/UterumVsOrangeri"));
const Storlek = lazy(() => import("./pages/Storlek"));
const Boyta = lazy(() => import("./pages/Boyta"));
const Tack = lazy(() => import("./pages/Tack"));
const FaqHub = lazy(() => import("./pages/FaqHub"));
const FaqArticle = lazy(() => import("./pages/FaqArticle"));
const Visualize = lazy(() => import("./pages/Visualize"));
const CategoryPillar = lazy(() => import("./pages/CategoryPillar"));
const Fabrik = lazy(() => import("./pages/Fabrik"));
const VaraPaket = lazy(() => import("./pages/Paket"));
const SommarPaket = lazy(() => import("./pages/SommarPaket"));
const ForlangdSommarPaket = lazy(() => import("./pages/ForlangdSommarPaket"));
const VarHostPaket = lazy(() => import("./pages/VarHostPaket"));
const VinterbonatPaket = lazy(() => import("./pages/VinterbonatPaket"));
const BoytaPaket = lazy(() => import("./pages/BoytaPaket"));
const BostandardPaket = lazy(() => import("./pages/BostandardPaket"));
const Omraden = lazy(() => import("./pages/Omraden"));
const Malardalen = lazy(() => import("./pages/Malardalen"));
const OmOss = lazy(() => import("./pages/OmOss"));
const DanielNystedt = lazy(() => import("./pages/DanielNystedt"));
const Integritetspolicy = lazy(() => import("./pages/Integritetspolicy"));
const SubpageWithSidebar = lazy(
  () => import("./components/SubpageWithSidebar")
);

function Router() {
  const [location] = useLocation();
  // SSR has no Suspense. A client-only boundary throws React #418
  // (updateSuspenseComponent) on the prerendered homepage.
  if (location === "/") {
    return <Home />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/uterum/" component={Uterum} />
        <Route path="/uterum/pris/">
          {() => (
            <SubpageWithSidebar>
              <Pris />
            </SubpageWithSidebar>
          )}
        </Route>
        <Route path="/uterum/bygglov/">
          {() => (
            <SubpageWithSidebar>
              <Bygglov />
            </SubpageWithSidebar>
          )}
        </Route>
        <Route path="/uterum/vinterbonat/">
          {() => (
            <SubpageWithSidebar>
              <Vinterbonat />
            </SubpageWithSidebar>
          )}
        </Route>
        <Route path="/uterum/glaspartier/">
          {() => (
            <SubpageWithSidebar>
              <Glaspartier />
            </SubpageWithSidebar>
          )}
        </Route>
        <Route path="/uterum/inspiration/">
          {() => (
            <SubpageWithSidebar>
              <Inspiration />
            </SubpageWithSidebar>
          )}
        </Route>
        <Route path="/uterum/grund/">
          {() => (
            <SubpageWithSidebar>
              <Grund />
            </SubpageWithSidebar>
          )}
        </Route>
        <Route path="/uterum/tak/">
          {() => (
            <SubpageWithSidebar>
              <Tak />
            </SubpageWithSidebar>
          )}
        </Route>
        <Route path="/uterum/uppvarmning/">
          {() => (
            <SubpageWithSidebar>
              <Uppvarmning />
            </SubpageWithSidebar>
          )}
        </Route>
        <Route path="/uterum/kondens/">
          {() => (
            <SubpageWithSidebar>
              <Kondens />
            </SubpageWithSidebar>
          )}
        </Route>
        <Route path="/uterum/oisolerat/">
          {() => (
            <SubpageWithSidebar>
              <Oisolerat />
            </SubpageWithSidebar>
          )}
        </Route>
        <Route path="/uterum/checklista/">
          {() => (
            <SubpageWithSidebar>
              <Checklista />
            </SubpageWithSidebar>
          )}
        </Route>
        <Route path="/uterum/uterum-vs-orangeri/">
          {() => (
            <SubpageWithSidebar>
              <UterumVsOrangeri />
            </SubpageWithSidebar>
          )}
        </Route>
        <Route path="/uterum/storlek/">
          {() => (
            <SubpageWithSidebar>
              <Storlek />
            </SubpageWithSidebar>
          )}
        </Route>
        <Route path="/uterum/boyta/">
          {() => (
            <SubpageWithSidebar>
              <Boyta />
            </SubpageWithSidebar>
          )}
        </Route>
        <Route path="/uterum/visualisera/">
          {() => (
            <SubpageWithSidebar>
              <Visualize />
            </SubpageWithSidebar>
          )}
        </Route>
        <Route path="/uterum/var-fabrik/">
          {() => (
            <SubpageWithSidebar>
              <Fabrik />
            </SubpageWithSidebar>
          )}
        </Route>
        <Route path="/uterum/vara-paket/" component={VaraPaket} />
        <Route path="/uterum/sommar-paket/" component={SommarPaket} />
        <Route
          path="/uterum/forlangd-sommar-paket/"
          component={ForlangdSommarPaket}
        />
        <Route path="/uterum/var-host-paket/" component={VarHostPaket} />
        <Route path="/uterum/vinterbonat-paket/" component={VinterbonatPaket} />
        <Route path="/uterum/boyta-paket/" component={BoytaPaket} />
        <Route path="/uterum/bostandard-paket/" component={BostandardPaket} />
        <Route path="/uterum/fragor/">{() => <FaqHub />}</Route>
        <Route path="/uterum/fragor/sida/:page/">
          {params => <FaqHub page={parseInt(params.page, 10)} />}
        </Route>
        <Route
          path="/uterum/fragor/kategori/:category/"
          component={CategoryPillar}
        />
        <Route path="/uterum/fragor/:slug/" component={FaqArticle} />
        <Route path="/referenser/uterum/" component={CaseHub} />
        <Route path="/uterum/omraden/" component={Omraden} />
        <Route path="/uterum/malardalen/" component={Malardalen} />
        <Route
          path="/uterum/stockholm/"
          component={() => <CityPage city="stockholm" />}
        />
        <Route
          path="/uterum/goteborg/"
          component={() => <CityPage city="goteborg" />}
        />
        <Route
          path="/uterum/malmo/"
          component={() => <CityPage city="malmo" />}
        />
        <Route
          path="/uterum/uppsala/"
          component={() => <CityPage city="uppsala" />}
        />
        <Route
          path="/uterum/vasteras/"
          component={() => <CityPage city="vasteras" />}
        />
        <Route
          path="/uterum/orebro/"
          component={() => <CityPage city="orebro" />}
        />
        <Route
          path="/uterum/linkoping/"
          component={() => <CityPage city="linkoping" />}
        />
        <Route
          path="/uterum/helsingborg/"
          component={() => <CityPage city="helsingborg" />}
        />
        <Route
          path="/uterum/eskilstuna/"
          component={() => <CityPage city="eskilstuna" />}
        />
        <Route
          path="/uterum/sodertalje/"
          component={() => <CityPage city="sodertalje" />}
        />
        <Route
          path="/uterum/nykoping/"
          component={() => <CityPage city="nykoping" />}
        />
        <Route
          path="/uterum/strangnas/"
          component={() => <CityPage city="strangnas" />}
        />
        <Route
          path="/uterum/enkoping/"
          component={() => <CityPage city="enkoping" />}
        />
        <Route
          path="/uterum/katrineholm/"
          component={() => <CityPage city="katrineholm" />}
        />
        <Route
          path="/uterum/sala/"
          component={() => <CityPage city="sala" />}
        />
        <Route
          path="/uterum/koping/"
          component={() => <CityPage city="koping" />}
        />
        <Route
          path="/uterum/arboga/"
          component={() => <CityPage city="arboga" />}
        />
        <Route
          path="/uterum/flen/"
          component={() => <CityPage city="flen" />}
        />
        <Route
          path="/uterum/gnesta/"
          component={() => <CityPage city="gnesta" />}
        />
        <Route
          path="/uterum/trosa/"
          component={() => <CityPage city="trosa" />}
        />
        <Route path="/om-oss/" component={OmOss} />
        <Route path="/om-oss/daniel-nystedt/" component={DanielNystedt} />
        <Route path="/integritetspolicy/" component={Integritetspolicy} />
        <Route path="/tack/" component={Tack} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <AppShell>
      <Router />
    </AppShell>
  );
}

export default App;
